import { useSyncExternalStore } from "react"
import { useTranslations as useNextIntlTranslations } from "next-intl"
import { encodeRenderKey } from "./render-key-sentinel"
import { getStampMode, subscribeStampMode } from "./stamp-mode"

/**
 * Drop-in replacement for next-intl's `useTranslations`, identical in type and —
 * for every logged-out visitor — identical at runtime (a pure passthrough).
 *
 * When an authenticated editor activates edit mode (`lib/intl/stamp-mode.ts`),
 * the returned `t()` appends an invisible render-key payload to each string it
 * produces, so the editor's activation-time stamp pass can read the exact
 * `namespace.key` off the rendered text node — replacing the value→key guess and
 * its "on this page" disambiguation radio with an exact lookup. `t.rich` /
 * `t.markup` / `t.raw` / `t.has` are passed through untouched (rich/markup yield
 * React nodes / pre-escaped markup we must not tamper with). (AHD-7514)
 *
 * Client components import `useTranslations` from here instead of `next-intl`;
 * server translation (`next-intl/server`) is inherently passthrough and stays as
 * is. A lint (ban-direct-intl) keeps client call sites pointed here so coverage
 * never drifts.
 */

// What we touch on a next-intl translator. The auxiliary methods are carried
// across verbatim, so they keep their real (stronger) next-intl types.
type Translator = ((
  key: string,
  values?: Record<string, unknown>,
  formats?: unknown
) => string) & {
  rich: unknown
  markup: unknown
  raw: unknown
  has: unknown
}

function makeStamped(t: Translator, namespace?: string): Translator {
  const fullKey = (key: string) => (namespace ? `${namespace}.${key}` : key)
  const stamped = ((key, values, formats) => {
    const out = t(key, values, formats)
    return out + encodeRenderKey(fullKey(key))
  }) as Translator
  stamped.rich = t.rich
  stamped.markup = t.markup
  stamped.raw = t.raw
  stamped.has = t.has
  return stamped
}

export const useTranslations: typeof useNextIntlTranslations = ((
  namespace?: string
) => {
  // useSyncExternalStore re-renders this consumer whenever stamp mode flips, so
  // activating the editor swaps `t` to the stamping variant across the tree.
  const on = useSyncExternalStore(
    subscribeStampMode,
    getStampMode,
    () => false // server snapshot: always passthrough, never stamp during SSR
  )
  const t = useNextIntlTranslations(namespace as never)
  if (!on) return t
  return makeStamped(t as unknown as Translator, namespace) as typeof t
}) as typeof useNextIntlTranslations
